import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import RolesRequest from '../../requests/vendor/RolesRequest';
import { 
  setSystemPermissionsLoading, 
  setSystemPermissions, 
  setSystemPermissionsError 
} from '../../redux/UserSlice';
import { FALLBACK_PERMISSIONS, transformApiPermissions } from '../../config/permissions';

//Todo => Custom hook to fetch and manage system permissions
export const useSystemPermissions = () => {
  const dispatch = useDispatch();
  const { 
    systemPermissions, 
    systemPermissionsLoading, 
    systemPermissionsError,
    isMainVendor 
  } = useSelector(state => state.user);

  //* Fetch system permissions from API with fallback
  const { data: apiPermissions, isLoading, error } = useQuery(
    'systemPermissions',
    RolesRequest.getSystemPermissions,
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 2,
      onError: (error) => {
        console.warn('Failed to fetch permissions from API, using fallback:', error);
        dispatch(setSystemPermissions(FALLBACK_PERMISSIONS));
      }
    }
  );

  //* Update Redux state based on API response
  useEffect(() => {
    dispatch(setSystemPermissionsLoading(isLoading));
    
    if (error) {
      dispatch(setSystemPermissionsError(error.message));
      dispatch(setSystemPermissions(FALLBACK_PERMISSIONS));
    } else if (apiPermissions?.success) {
      // Transform API permissions to frontend format for UI components
      const transformedPermissions = transformApiPermissions(apiPermissions.data);
      dispatch(setSystemPermissions({
        raw: apiPermissions.data, // Keep original API structure for backend calls
        transformed: transformedPermissions // Simplified format for UI
      }));
    } else if (apiPermissions && !apiPermissions.success) {
      dispatch(setSystemPermissionsError(apiPermissions.message));
      dispatch(setSystemPermissions(FALLBACK_PERMISSIONS));
    }
  }, [apiPermissions, isLoading, error, dispatch]);

  //* Return the current permissions (API or fallback)
  const currentPermissions = systemPermissions?.transformed || systemPermissions || FALLBACK_PERMISSIONS;
  const rawPermissions = systemPermissions?.raw || null;

  return {
    permissions: currentPermissions,
    rawPermissions, // For backend API calls that need permission slugs
    isLoading: systemPermissionsLoading,
    error: systemPermissionsError,
    isMainVendor,
    //* Helper methods => Get module permissions
    getModulePermissions: (module) => currentPermissions[module] || [],
    getAllModules: () => Object.keys(currentPermissions),
    hasModule: (module) => !!currentPermissions[module],
    //* Get permission slugs for backend calls
    getPermissionSlugs: (module, actionNames = []) => {
      if (!rawPermissions || !rawPermissions[module]) return [];
      return rawPermissions[module]
        .filter(perm => actionNames.includes(perm.name))
        .map(perm => perm.permission_slug);
    }
  };
};